import { CalendarEvent } from '../../types/calendar';
import { isToday, getEventCardStyle } from '../../utils/calendar';
import { formatTime, formatDate } from '../../utils/time';
import { Icon } from '../../utils/icons';
import MemberAvatar from '../MemberAvatar';

interface MobileEventCardProps {
  event: CalendarEvent;
  onClick: () => void;
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
}

function MobileEventCard({ 
  event, 
  onClick,
  onSwipeLeft,
  onSwipeRight
}: MobileEventCardProps) {
  const startTime = new Date(event.startTime);
  const endTime = new Date(event.endTime);
  
  const formatDateShort = (date: Date): string => {
    if (isToday(date)) {
      return 'Today';
    }
    // Use formatDate utility but extract just month and day for short format
    return formatDate(date, 'short').replace(/, \d{4}$/, ''); // Remove year from short format
  };

  const isAllDay = event.isAllDay === true || event.isImportantDate || 
    (startTime.getHours() === 0 && startTime.getMinutes() === 0 && 
     endTime.getHours() === 23 && endTime.getMinutes() === 59);

  const timeDisplay = isAllDay 
    ? 'All Day' 
    : `${formatTime(startTime)} - ${formatTime(endTime)}`;

  const dateDisplay = formatDateShort(startTime);

  // Support both legacy (single member) and new (multiple members) format
  const members = event.members && event.members.length > 0
    ? event.members.map(m => ({
        ...m,
        memberColor: m.memberColor || '#3b82f6', // Ensure memberColor is always set
      }))
    : (event.memberId ? [{
        memberId: event.memberId,
        memberName: event.memberName || '',
        memberColor: event.memberColor || '#3b82f6',
        memberAvatar: event.memberAvatar,
      }] : []);

  const cardStyle = getEventCardStyle(members, false);
  const hasLeftBar = cardStyle.leftBarGradient;
  const leftBarWidth = cardStyle.leftBarWidth ? parseInt(cardStyle.leftBarWidth.replace('px', '')) : 0;
  const paddingLeft = hasLeftBar && leftBarWidth > 0 ? `${leftBarWidth + 12}px` : undefined;

  return (
    <div
      onClick={onClick}
      className="rounded-landing p-3 mb-2 shadow-sm border border-gray-200 active:bg-gray-50 min-h-[80px] touch-manipulation relative"
      style={{
        background: cardStyle.background || 'rgba(255, 255, 255, 0.8)',
        paddingLeft: paddingLeft,
      }}
    >
      {/* Left bar gradient overlay for multi-member events */}
      {hasLeftBar && (
        <div
          className="absolute left-0 top-0 bottom-0"
          style={{
            width: cardStyle.leftBarWidth || '4px',
            background: cardStyle.leftBarGradient,
            borderTopLeftRadius: '18px', // Match rounded-landing (18px)
            borderBottomLeftRadius: '18px', // Match rounded-landing (18px)
            zIndex: 1,
          }}
        />
      )}
      <div className="flex items-start justify-between gap-2 relative z-10">
        <div className="flex-1 min-w-0">
          {/* Title */}
          <h3 className="text-base font-semibold text-gray-900 mb-0.5 truncate">
            {event.title}
          </h3>
          
          {/* Time and Date */}
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm text-gray-600">{timeDisplay}</span>
            {!isToday(startTime) && (
              <span className="text-xs text-gray-500">• {dateDisplay}</span>
            )}
          </div>
          
          {/* Members */}
          {members && members.length > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap">
              {members.slice(0, 3).map((member, idx) => (
                <div
                  key={member.memberId || idx}
                  className="flex items-center gap-1.5"
                >
                  <MemberAvatar
                    member={member}
                    size="sm"
                  />
                  <span className="text-xs text-gray-600">{member.memberName}</span>
                </div>
              ))}
              {members.length > 3 && (
                <span className="text-xs text-gray-500">
                  +{members.length - 3} more
                </span>
              )}
            </div>
          )}
          
          {/* Location */}
          {event.location && (
            <div className="mt-2 flex items-center gap-1.5">
              <Icon name="location" size="sm" className="text-gray-600" />
              <span className="text-xs text-gray-600 truncate">{event.location}</span>
            </div>
          )}
        </div>
        
        {/* Priority indicator */}
        {event.priority && event.priority !== 'low' && (
          <div className={`flex-shrink-0 w-2 h-2 rounded-sm-full ${
            event.priority === 'critical' ? 'bg-red-500' :
            event.priority === 'high' ? 'bg-orange-500' :
            'bg-yellow-500'
          }`} />
        )}
      </div>
    </div>
  );
}

export default MobileEventCard;
